import { CommonDialogs } from "@mcleod/common";
import { Checkbox, ClickEvent, DataSourceAction, DataSourceExecutionEvent, DataSourceMode, DataSourceModeChangeEvent, Layout, LookupModelSearchEvent, LookupModelSelectionEvent, TableSelectionEvent, Textbox } from "@mcleod/components";
import { Api, LogManager, Model, ModelRow, ModelSearchResult, UrlUtil } from "@mcleod/core";
import { AutogenLayoutMobileCommExAppControl } from "./autogen/AutogenLayoutMobileCommExAppControl";

const log = LogManager.getLogger("lme.dispatch.mobilecomm.MobileCommExAppControl");

/**
 * Form for managing mobile communication configurations and field layouts
 */
export class MobileCommExAppControl extends AutogenLayoutMobileCommExAppControl {
    public textboxToCheckboxMap: Map<string, Checkbox[]> = new Map();
    public textboxGroupMap: Map<number, Textbox[]> = new Map();
    private selectionLayoutExpanded: boolean = true;

    override async onLoad() {
        this.loadFromUrl();
        this.setupValidationRules();
        this.setupFormIdTooltip()
        this.setupEventListeners();
        this.textboxToCheckboxMap = this.buildTextboxToCheckboxMap();
        this.textboxGroupMap = this.groupConsecutiveTextboxes();
    }

    /**
     * Set up event listeners for the form
     */
    private setupEventListeners(): void {
        // Set up data source listeners
        if (this.sourceMCExAppControl) {
            this.sourceMCExAppControl.addAfterModeChangeListener(this.onModeChange.bind(this));
        }
    }

    private onModeChange(event: DataSourceModeChangeEvent): void {
        const mode = event.newMode;

        switch (mode) {
            case DataSourceMode.ADD:
                this.textboxMcControlId.focus();
                break;
        }
    }

    /**
     * Groups consecutive Textbox components from getRecursiveChildren.
     * @returns Map where keys are group indices and values are arrays of consecutive Textbox components
     */
    private groupConsecutiveTextboxes(): Map<number, Textbox[]> {
        const textboxGroupsMap = new Map<number, Textbox[]>();
        const allChildren = this.getRecursiveChildren();
        let currentGroup: Textbox[] = [];
        let groupIndex = 0;

        for (const component of allChildren) {
            if (component instanceof Textbox) {
                currentGroup.push(component);
            } else {
                // Non-textbox component found, save current group if it exists
                if (currentGroup.length > 0) {
                    textboxGroupsMap.set(groupIndex, currentGroup);
                    groupIndex++;
                    currentGroup = [];
                }
            }
        }

        // Don't forget to add the last group if it exists
        if (currentGroup.length > 0) {
            textboxGroupsMap.set(groupIndex, currentGroup);
        }

        return textboxGroupsMap;
    }

    /**
     * Finds and returns the group of Textbox components that contains a Textbox with the specified ID.
     * @param id The ID of the Textbox to search for
     * @returns The array of Textbox components containing the specified Textbox, or undefined if not found
     */
    public findTextboxGroup(id: string): Textbox[] | undefined {
        for (const textboxGroup of this.textboxGroupMap.values()) {
            if (textboxGroup.some(textbox => textbox.id === id)) {
                return textboxGroup;
            }
        }
        return undefined;
    }

    /**
     * Creates a map of Textbox component IDs to their previous Checkbox components.
     * @returns Map where keys are Textbox component IDs and values are arrays of all Checkbox components before that textbox
     */
    private buildTextboxToCheckboxMap(): Map<string, Checkbox[]> {
        const textboxCheckboxMap = new Map<string, Checkbox[]>();
        const allChildren = this.getRecursiveChildren();
        let checkboxes: Checkbox[] = [];
        let resetCheckbox = false;

        for (const component of allChildren) {
            if (component instanceof Checkbox) {
                if (resetCheckbox) {
                    checkboxes = [];
                }
                checkboxes.push(component);
                resetCheckbox = false;
            }

            if (component instanceof Textbox && component.id && checkboxes.length > 0) {
                if (component.id === 'textboxLoadNumberField') {
                    const checkbox = checkboxes.find(cb => cb.id === 'checkboxCheckLoadNumber');
                    if (checkbox) {
                        textboxCheckboxMap.set(component.id, [checkbox]);
                    }
                } else if (component.id === 'textFieldOrderNumber' || component.id === 'textFieldAccepted') {
                    const checkbox = checkboxes.find(cb => cb.id === 'checkboxUpdatePtaDate');
                    if (checkbox) {
                        textboxCheckboxMap.set(component.id, [checkbox]);
                    }
                } else if (component.id === 'textboxOdometer') {
                    const checkbox = checkboxes.find(cb => cb.id === 'checkboxUpdateOdometer');
                    if (checkbox) {
                        textboxCheckboxMap.set(component.id, [checkbox]);
                    }
                } else if (component.id === 'textboxFormNumber') {
                    const checkbox = checkboxes.find(cb => cb.id === 'checkboxSendNoLoadMessage');
                    if (checkbox) {
                        textboxCheckboxMap.set(component.id, [checkbox]);
                    }
                } else if (component.id === 'textboxLoginIdField') {
                    const checkbox = checkboxes.find(cb => cb.id === 'checkboxDirectedMessagesByLoginId');
                    if (checkbox) {
                        textboxCheckboxMap.set(component.id, [checkbox]);
                    }
                } else if (component.id === 'textboxTrailerEmptyField') {
                    const checkbox = checkboxes.find(cb => cb.id === 'checkboxUpdateTrailerEmptyStatus');
                    if (checkbox) {
                        textboxCheckboxMap.set(component.id, [checkbox]);
                    }
                } else if (component.id === 'textboxArrivalDateField' || component.id === 'textboxArrivalTimeField') {
                    const checkbox = checkboxes.find(cb => cb.id === 'checkboxUpdateArrival');
                    if (checkbox) {
                        textboxCheckboxMap.set(component.id, [checkbox]);
                    }
                } else {
                    textboxCheckboxMap.set(component.id, checkboxes);
                }
                resetCheckbox = true;
            }
        }

        return textboxCheckboxMap;
    }

    /**
     * Sets up validation rules for the main data source before post operations.
     * Validates that all field index values are within valid range.
     */
    private setupValidationRules(): void {
        this.mainDataSource.doBeforeDataSourcePost = async () => {

            if (this.mainDataSource.mode == DataSourceMode.ADD) {
                const response = await Api.search("lme/dispatch/mobilecomm/mc-exapp-control", {
                    "id": this.mainDataSource.activeRow?.get("id"),
                    "mc_control_id": this.mainDataSource.activeRow?.get("mc_control_id"),
                    "form_type": "I"
                });

                if (response.data.length > 0) {
                    await CommonDialogs.showError("An External Application Control with the selected Vendor and inbound Form Number already exists.");
                    return false;
                }
            }

            const layouts = [
                this.layoutMobileCommControlLoad,
                this.layoutMobileCommControlDriver,
                this.layoutMobileCommControlSendLoad,
                this.layoutMobileCommControlTractor,
                this.layoutMobileCommControlMessage,
                this.layoutMobileCommControlTrailerTracking
            ];

            for (const layout of layouts) {
                if (layout && !layout.validateAllFieldIndices()) {
                    return false;
                }
            }

            return true;
        };
    }

    /**
     * Handles completion of form field options data source execution
     */
    sourceMobileCommExAppControlAfterExecution(event: DataSourceExecutionEvent) {
        if (!this.mainDataSource?.activeRow) {
            this.layoutMobileCommExAppSelectionList.buttonEdit.visible = false;
            return;
        }

        try {
            this.refreshFormFieldTable();
            this.setupFormIdTooltip();
            this.initializeSelectionTable();
        } catch (error) {
            log.error("Error initializing form control", error);
        }
    }

    private loadFromUrl() {
        const urlProps = UrlUtil.getPropsFromUrl(document.location.search);

        if (!urlProps || !urlProps["mc_control_id"] || !urlProps["id"]) {
            return;
        }

        Model.search("lme/dispatch/mobilecomm/mc-control", {
            id: urlProps["mc_control_id"],
            _field_list: {layoutName: "lme/dispatch/mobilecomm/MobileCommExAppControl"}
        }).then((result: ModelSearchResult) => {
            const vendor_name = result.modelRows[0]?.getLookupModelData("vendor_name");

            this.mainDataSource.search({
                id: urlProps["id"],
                mc_control_id: urlProps["mc_control_id"]
            }).then(async (result2) => {
                if (result2.actualRowCount == 0) {
                    const newRow = await this.mainDataSource?.createBlankRow();
                    newRow.set("mc_form.title", urlProps["title"]);
                    newRow.set("id", urlProps["id"]);
                    newRow.setValueAndLookupModelData("mc_control_id", urlProps["mc_control_id"], vendor_name);
                    this.mainDataSource.setRowsAndMode(DataSourceMode.ADD, [newRow]);
                    this.textboxMcControlId.enabled = urlProps["mc_control_id"] ? false : true;
                }
            });
        });
    }

    /**
     * Refreshes the form field table with parsed form definition data
     */
    public refreshFormFieldTable(formFields?: any[]): void {
        if (!formFields) {
            formFields = this.activeRow.get("form_fields", []);
        }

        this.tableFormFieldOpt.clearRows();
        this.panelCollapseForm.visible = formFields.length > 0;
        if (formFields.length > 0) {
            const formFieldsRows = formFields.map((data) => this.createFormFieldRow(data));
            this.tableFormFieldOpt.displayData(null, formFieldsRows, 0);
            this.splitterMain.position = "25%";
        } else {
            this.splitterMain.position = "0%";
        }
    }

    private createFormFieldRow(data) {
        return new ModelRow(this.mainDataSource.url, false, data);
    }

    /**
     * Configures the dynamic tooltip for the form ID textbox
     */
    public async setupFormIdTooltip(): Promise<void> {
        try {
            this.textboxMcFormid.tooltipDynamicContent = async () => {
                if (this.textboxMcFormid.text != null && this.textboxMcFormid.text !== "") {
                    const layout = await Layout.getLoadedLayout("lme/dispatch/mobilecomm/MobileCommFormQuickInfo") as Layout;
                    await layout.mainDataSource?.search({
                        id: this.mainDataSource.activeRow.get("id"),
                        mc_control_id: this.mainDataSource.activeRow.get("mc_control_id"),
                        form_type: "I"
                    });
                    return layout;
                }
            };
        } catch (error) {
            log.error("Error setting up form ID tooltip", error);
        }
    }

    /**
     * Highlights a row in tableFormFieldOpt that matches the given sequence number.
     * @param sequence The sequence number to search for
     * @returns true if a matching row was found and highlighted, false otherwise
     */
    public highlightRowBySequence(sequence: number): boolean {
        let found = false;
        const rows = this.tableFormFieldOpt.rows;

        for (const row of rows) {
            const rowSequence = row.data.get("sequence");
            row.selected = false;
            if (rowSequence != null && Number(rowSequence) == sequence) {
                row.selected = true;
                row.scrollIntoView();
                found = true;
            }
        }

        return found;
    }

    tableFormFieldOptOnSelect(event: TableSelectionEvent) {
        const selectedRow = event.newRow;
        const rows = this.tableFormFieldOpt.rows;

        for (const row of rows) {
            const rowSequence = row.data.get("sequence");
            if (selectedRow && rowSequence != null && Number(rowSequence) !== Number(selectedRow.data.get("sequence"))) {
                row.selected = false;
            }
        }

        if (selectedRow) {
            const sequence = selectedRow.data.get("sequence");

            // Find and focus the textbox with matching sequence value
            const allChildren = this.tabsetMobileCommControl.getRecursiveChildren();
            for (const component of allChildren) {
                if (component instanceof Textbox && component.text && Number(component.text) === Number(sequence)) {
                    component.focus();
                    break;
                }
            }
        }
    }

    sourceMobileCommFormBeforeExecution(event: DataSourceExecutionEvent): void {
        if (event.getAction() === DataSourceAction.SEARCH) {
            this.applyUrlFiltersToSearch(event);
        }
    }

    /**
     * Applies URL parameters as filters to search events
     */
    private applyUrlFiltersToSearch(event: DataSourceExecutionEvent): void {
        const urlProps = UrlUtil.getPropsFromUrl(window.location.search);
        if (!urlProps) return;

        if (urlProps.formType != null) {
            event.filter.form_type = urlProps.formType;
        }
        if (urlProps.mcControlId != null) {
            event.filter.mc_control_id = urlProps.mcControlId;
        }
    }

    private initializeSelectionTable(): void {
        if (this.layoutName == "lme/dispatch/mobilecomm/MobileCommExAppControl") {
            this.layoutMobileCommExAppSelectionList.buttonEdit.visible = false;
        }

        this.layoutMobileCommExAppSelectionList.mainDataSource?.search({
            form_id: this.mainDataSource?.activeRow?.get("id"),
            mc_control_id: this.mainDataSource?.activeRow?.get("mc_control_id")
        });
    }

    /** This is an event handler for the onClick event of buttonExpandSelections. */
    async buttonExpandSelectionsOnClick(event: ClickEvent): Promise<void> {
        const transitionProps = { speed: 100, paddingTop: 0 };

        if (this.selectionLayoutExpanded) {
            this.buttonExpandSelections.imageRotation = 90;
            await this.layoutMobileCommExAppSelectionList.collapse(transitionProps);
        } else {
            this.buttonExpandSelections.imageRotation = 0;
            await this.layoutMobileCommExAppSelectionList.expand(transitionProps);
        }

        this.selectionLayoutExpanded = !this.selectionLayoutExpanded;
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxMcControlId. */
    textboxMcControlIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.is_active = 'Y';
    }

    textboxMcFormIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.form_type = "I";

        if (this.textboxMcControlId.text) {
            event.filter.mc_control_id = this.textboxMcControlId.text;
            event.filter.is_active = 'Y';
        }
    }

    /**
     * Handles vendor selection changes.
     * Enables the form ID field when a vendor is selected in add/edit mode and clears dependent fields.
     */
    async textboxMcControlIdAfterLookupModelSelection(event: LookupModelSelectionEvent): Promise<void> {
        const isEditMode = this.mainDataSource.mode !== DataSourceMode.SEARCH;

        // Enable form ID field only when vendor is selected in edit mode
        this.textboxMcFormid.enabled = isEditMode && event.hasSelection();

        // Clear dependent fields when vendor changes in edit mode
        if (isEditMode) {
            await this.clearFormSelection();
            await this.textboxMcFormid.focus();
            this.tableFormFieldOpt.clearRows();
        }
    }

    /**
     * Clears form selection and related data.
     */
    private async clearFormSelection(): Promise<void> {
        this.textboxMcFormid.clear();
    }

    async textboxMcFormIdAfterLookupModelSelection(event: LookupModelSelectionEvent) {
        if (this.mainDataSource.mode != DataSourceMode.SEARCH && event.hasSelection() === true) {
            const response = await Api.search("lme/dispatch/mobilecomm/mc-exapp-control", {
                "id": event.selection?.get("id"),
                "mc_control_id": this.mainDataSource.activeRow?.get("mc_control_id")
            });

            if (response.data.length > 0) {
                this.textboxMcFormid.validationWarning =
                    "An External Application Control with the selected Vendor and inbound Form Number already exists.";

                this.tableFormFieldOpt.clearRows();
            } else {
                this.textboxMcFormid.validationWarning = null;

                const formResponse = await Api.search("lme/dispatch/mobilecomm/mc-form", {
                    "id": event.selection?.get("id"),
                    "mc_control_id": this.mainDataSource.activeRow?.get("mc_control_id"),
                    "form_type": "I"
                });

                if (formResponse.data.length > 0) {
                    await this.refreshFormFieldTable(formResponse.data[0].form_fields);
                }
            }
        }
    }
}
